import { z } from 'zod';

import {
  DisbursementId,
  DrawId,
  FeeId,
  LiquidationId,
  LoanId,
  PaymentId,
  PayoffId,
  PrincipalModificationId,
  WorkoutPlanId,
  WriteoffId,
} from '../BrandedIds';
import { zodJSMixedDate } from '../dates/BrandedDate';

export const LoanPaymentStatus = z.enum(['CURRENT', 'PASTDUE']);
export type LoanPaymentStatus = z.infer<typeof LoanPaymentStatus>;

/**
 * Total deferred principal + interest for a loan (includes active/completed workout plan deferred p&i)
 * Principal includes capitalized amounts (see flex mod which adds the entire deferred balance to principal)
 */
export const DeferredAmounts = z.object({
  principal: z.number(),
  interest: z.number(),
});
export type DeferredAmounts = z.infer<typeof DeferredAmounts>;

// These balances are stored as JSON, and rows written before deferredAmounts existed lack the
// key. Derive it from the older deferredPrincipalBalance so the field can stay required below.
// Use preprocess, not a trailing .transform(): zod-openapi (leaf-api's public docs) rejects a
// transform in an output schema but serializes this plain object fine.
export const PaymentSnapshotBalances = z.preprocess(
  (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const balances = raw as Record<string, unknown>;
      if (balances.deferredAmounts === undefined) {
        return {
          ...balances,
          deferredAmounts: {
            principal: typeof balances.deferredPrincipalBalance === 'number' ? balances.deferredPrincipalBalance : 0,
            interest: 0,
          },
        };
      }
    }
    return raw;
  },
  z.object({
    statementSuspenseBalance: z.number(),
    futureStatementSuspenseBalance: z.number(),
    unappliedSuspenseBalance: z.number().default(0),

    escrowBalance: z.number(),
    netEscrowBalance: z.number(),
    reserveBalance: z.number().optional(),
    feeBalance: z.number().optional(),
    holdbackBalance: z.number().optional(), // TODO make required
    lossDraftBalance: z.number().optional(),
    feesOutstanding: z.number().optional(), // excludes deferred fees
    feesOutstandingIncludingDeferred: z.number().optional(),
    advancesPrincipalOutstanding: z.number().optional(),
    advancesPrincipalOutstandingIncludingDeferred: z.number().optional(),
    principalPrepaymentBalance: z.number(), // this is not a real balance, more of a total
    /**
     * @deprecated Use deferredAmounts.principal instead. Retained only for backwards compatibility on the
     * public REST API; it is no longer persisted and is derived from deferredAmounts.principal at the API boundary.
     */
    deferredPrincipalBalance: z.number().optional().meta({ deprecated: true }),
    deferredAmounts: DeferredAmounts,
  }),
);
export type PaymentSnapshotBalances = z.infer<typeof PaymentSnapshotBalances>;

export const PaymentSnapshotTotalPaid = z.object({
  sum: z.number(),
  principal: z.number(),
  interest: z.number(),
  escrow: z.number(),
  fee: z.number(),
  advance: z.number(),
  advancePrincipal: z.number().optional(),
  advanceInterest: z.number().optional(),
  holdback: z.number().optional(),
  lossDraft: z.number().optional(),
});
export type PaymentSnapshotTotalPaid = z.infer<typeof PaymentSnapshotTotalPaid>;

export const PaymentSnapshot = z.object({
  outstandingBalance: z.number(),
  totalPaid: PaymentSnapshotTotalPaid,
  balances: PaymentSnapshotBalances,
  status: LoanPaymentStatus,
});
export type PaymentSnapshot = z.infer<typeof PaymentSnapshot>;

export const BalancePaymentSnapshot = PaymentSnapshot.omit({ status: true }).extend({
  previousOutstandingBalance: z.number(),
});
export type BalancePaymentSnapshot = z.infer<typeof BalancePaymentSnapshot>;

const BoardingEvent = z.object({
  type: z.literal('boarding'),
  id: LoanId,
});

const PaymentEvent = z.object({
  type: z.literal('payment'),
  id: PaymentId,
});

const DisbursementEvent = z.object({
  type: z.literal('disbursement'),
  id: DisbursementId,
});

const DrawEvent = z.object({
  type: z.literal('draw'),
  id: DrawId,
});

const PayoffEvent = z.object({
  type: z.literal('payoff'),
  id: PayoffId,
});

const AdvanceEvent = z.object({
  type: z.literal('advance'),
  id: FeeId,
});

const FeeEvent = z.object({
  type: z.literal('fee'),
  id: FeeId,
});

const ContructionToPermanentEvent = z.object({
  type: z.literal('constructionToPermanent'),
  id: LoanId,
});

const PaymentDeferralWorkoutPlanEvent = z.object({
  type: z.literal('paymentDeferralWorkoutPlan'),
  id: WorkoutPlanId,
});

const PrincipalModificationEvent = z.object({
  type: z.literal('principalModification'),
  id: PrincipalModificationId,
});

const FlexModificationEvent = z.object({
  type: z.literal('flexModification'),
  id: PrincipalModificationId,
});

const WriteoffEvent = z.object({
  type: z.literal('writeoff'),
  id: WriteoffId,
});

const LiquidationEvent = z.object({
  type: z.literal('liquidation'),
  id: LiquidationId,
});

const TransferEvent = z.object({
  type: z.literal('transfer'),
  id: LoanId,
});

const BalanceEvent = z.discriminatedUnion('type', [
  BoardingEvent,
  PaymentEvent,
  DisbursementEvent,
  DrawEvent,
  PayoffEvent,
  AdvanceEvent,
  FeeEvent,
  ContructionToPermanentEvent,
  PaymentDeferralWorkoutPlanEvent,
  PrincipalModificationEvent,
  FlexModificationEvent,
  WriteoffEvent,
  LiquidationEvent,
  TransferEvent,
]);
export type BalanceEvent = z.infer<typeof BalanceEvent>;

export const BalanceSnapshot = z.object({
  transactionDate: zodJSMixedDate,
  snapshot: BalancePaymentSnapshot,
  event: BalanceEvent,
});
export type BalanceSnapshot = z.infer<typeof BalanceSnapshot>;
